<!-- UserInterests.vue -->
<script setup>
import { ref, computed, onMounted } from 'vue'
import { fetchWrapper } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const keywords = ref([])
const loading = ref(true)
const error = ref(null)
const saving = ref(false)
const saveError = ref(null)
const saveSuccess = ref(false)
const filterText = ref('')

// Samsung Keyboard fix: update filterText on all input events
function onFilterInput(e) {
  filterText.value = e.target.value
}

const selectedCount = computed(() => {
  return keywords.value.filter(k => k.is_selected).length
})

// Computed property for filtered keywords - now searches localized names
const filteredKeywords = computed(() => {
  const filter = filterText.value.trim().toLowerCase()
  if (!filter) return keywords.value
  
  return keywords.value.filter(k => {
    // Search in the localized display name
    const localizedName = t(`curriculum-keyword.name.${k.id}`).toLowerCase()
    
    // Also search in the raw name and label if they exist
    const rawName = k.name ? k.name.toLowerCase() : ''
    const label = k.label ? k.label.toLowerCase() : ''
    
    return localizedName.includes(filter) || 
           rawName.includes(filter) || 
           label.includes(filter)
  })
})

const fetchKeywords = async () => {
  try {
    loading.value = true
    error.value = null
    const response = await fetchWrapper.get('/curriculum/keywords/')
    keywords.value = response
  } catch (err) {
    error.value = 'Failed to load interests. Please try again.'
    console.error('Error fetching keywords:', err)
  } finally {
    loading.value = false
  }
}

const toggleInterest = async (keyword) => {
  keyword.is_selected = !keyword.is_selected
  saveSuccess.value = false
  await saveInterests()
}

const saveInterests = async () => {
  try {
    saving.value = true
    saveError.value = null
    saveSuccess.value = false
    
    const selectedIds = keywords.value
      .filter(k => k.is_selected)
      .map(k => k.id)
      
    await fetchWrapper.post('/curriculum/keywords/update_interests/', {
      keyword_ids: selectedIds
    })
    
    saveSuccess.value = true
  } catch (err) {
    saveError.value = 'Failed to save interests. Please try again.'
    console.error('Error saving interests:', err)
  } finally {
    saving.value = false
  }
}

// Fetch keywords when component is mounted
onMounted(() => {
  fetchKeywords()
})

const iconImages = import.meta.glob('@/assets/images/icons/*.png', { eager: true, import: 'default' })

function getIconSrc(keywordName) {
  // Adjust the path to match the glob key
  keywordName = keywordName.replace(' ', '-').replace('/', '-')
  const key = `/src/assets/images/icons/${keywordName}.png`
  return iconImages[key] || ''
}
</script>

<template>
  <div class="container mb-5">
    <div class="user-interests">
      <h5 class="fnt-weight-600 mb-4">{{ t('register.choose-your-interests') }}</h5>
      
      <div v-if="loading" class="text-gray-600">
        {{ t('curriculum.loading-interests---') }}
      </div>
      
      <div v-else-if="error" class="text-red-600">
        {{ error }}
      </div>
      
      <div v-else>
        <div class="input-group mb-3">
          <span class="input-group-text">
            <i class="bi bi-search"></i>
          </span>
          <input
            type="text"
            class="form-control"
            :placeholder="t('register.search')"
            v-model="filterText"
            @input="onFilterInput"
            @change="onFilterInput"
            @compositionend="onFilterInput"
          />
        </div>
        <div class="dropdown-menu show" style="position: static; float: none;">
          <div v-if="filteredKeywords.length === 0" class="dropdown-item d-flex align-items-center" style="cursor: pointer;">
            {{ t('register.no-results-found') }}
          </div>
          <template v-for="(keyword, idx) in filteredKeywords" :key="keyword.id">
            <div
              class="dropdown-item d-flex align-items-center"
              style="cursor: pointer;"
              @click="toggleInterest(keyword)"
            >
              <img
                :src="getIconSrc(keyword.name)"
                width="50px"
                style="margin-right: 10px"
                @error="event => event.target.style.display = 'none'"
                alt=""
              />
              <span>{{ t(`curriculum-keyword.name.${keyword.id}`) }}</span>
              <span v-if="keyword.is_selected" class="ms-auto text-success"><i class="bi bi-check-circle-fill"></i></span>
            </div>
            <div v-if="idx < filteredKeywords.length - 1" class="dropdown-divider"></div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.text-white div h5 {
  color: white !important;
}
.card-body {
  padding: 0.1rem;
}

.flex-grow-1 {
  flex-grow: 1;
}
.badge-cs{
  border: 1px solid #dfdfdf;
  font-size: 14px;
}
.fs-18px{
  font-size:18px;
}
.dropdown-menu.show {
  max-height: 330px;
  min-height: 330px;
  overflow-x: auto;
}
</style>